import { Alert, Badge, Card, Table } from "react-bootstrap";
import { ArrowDown, ArrowUp, BarChartFill, Dash, DatabaseFill, ExclamationCircleFill, PeopleFill } from "react-bootstrap-icons";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import teamsIcon from "@/assets/microsoft-teams.png";
import { formatBytes } from "@/pages/Admin/SharePointInventory/format";
import { comparisonWidths, formatHealthChange, healthChangeTone, healthMetrics, orderDemoResources, snapshotLabel } from "./resourceHealthDemo";
import type { HealthMetric, SnapshotResult } from "./resourceHealthDemo";
import styles from "./ResourceHealthDemoResults.module.scss";

const summaryKeys = new Set(["directComplianceScore", "resourcesOwned", "storageOwned", "nonCompliantResourcesOwned"]);

function Change({ metric, current, previous }: { metric: HealthMetric; current: number | null | undefined; previous: number | null | undefined }) {
  const direction = current == null || previous == null ? null : Math.sign(current - previous);
  const Icon = direction === null || direction === 0 ? Dash : direction > 0 ? ArrowUp : ArrowDown;
  return <span className={`${styles.change} ${styles[healthChangeTone(metric, current, previous)]}`}>
    <Icon aria-hidden="true" />
    {direction === 0 ? "No change" : formatHealthChange(metric, current, previous)}
  </span>;
}

function ScoreRing({ value }: { value: number | null | undefined }) {
  const progress = value == null ? 0 : Math.max(0, Math.min(100, value));
  return <div className={styles.scoreRing}>
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="42" className={styles.ringTrack} />
      <circle cx="50" cy="50" r="42" pathLength="100" className={styles.ringValue} strokeDasharray={`${progress} 100`} />
    </svg>
    <strong>{value == null ? "N/A" : `${value.toFixed(1)}%`}</strong>
  </div>;
}

function VisualComparison({ metric, results }: { metric: HealthMetric; results: SnapshotResult[] }) {
  const values = results.map((result) => result.person?.[metric.key]);
  const widths = comparisonWidths(metric, values[0], values[1]);
  return <div className={`${styles.bars} ${styles[metric.kind]}`}>
    {results.map((result, index) => <div className={styles.barRow} key={result.snapshot.snapshotId}>
      <span className="visually-hidden">{snapshotLabel(result.snapshot)}: </span>
      <div className={styles.barTrack} aria-hidden="true">
        {widths[index] != null && <span className={index === 0 ? styles.currentBar : styles.previousBar} style={{ width: `${widths[index]}%` }} />}
      </div>
      <span className={styles.barLabel}>{metric.format(values[index])}</span>
    </div>)}
  </div>;
}

function ResourcePanel({ result, previous }: { result: SnapshotResult; previous: boolean }) {
  const resources = orderDemoResources(result.resources);
  return <Card className={`${styles.resourcePanel} ${previous ? styles.previousPanel : ""}`}>
    <Card.Body>
      <div className={styles.panelHeading}>
        <PeopleFill className={styles.panelIcon} aria-hidden="true" />
        <div className={styles.panelTitle}>
          <h4>Your owned resources</h4>
          <p>{snapshotLabel(result.snapshot)}</p>
        </div>
        <span className={styles.resourceCount}>{resources.length.toLocaleString()} resources</span>
      </div>
      {!result.person ? <Alert variant="info" className="mb-0">This person is not present in this snapshot. Missing data is not a zero score.</Alert> : <>
        <p className={styles.panelDescription}>
          Resources you directly owned in this snapshot, sorted by health (lowest first).
          {result.person.businessUnit && ` Business unit: ${result.person.businessUnit}.`}
        </p>
        <div className={styles.resourceScroll}>
          <Table bordered size="sm" className={styles.resourceTable}>
            <thead><tr><th>Resource</th><th>Type</th><th>Health</th><th>Storage</th><th>Missing metadata / Review</th></tr></thead>
            <tbody>{resources.map((resource, index) => <tr key={`${resource.resourceId ?? resource.url ?? resource.title}-${index}`}>
              <td>{resource.title || "Untitled resource"}</td>
              <td>
                {resource.resourceType === "TeamSite" ? <img src={teamsIcon} className={styles.resourceTypeIcon}
                  alt="Microsoft Teams (TeamSite)" title="Microsoft Teams (TeamSite)" />
                  : resource.resourceType === "GroupConnectedSite" ? <span role="img" aria-label="Outlook group (GroupConnectedSite)"
                    title="Outlook group (GroupConnectedSite)" className={styles.outlookIcon}>
                    <PiMicrosoftOutlookLogoFill className={styles.resourceTypeIcon} aria-hidden="true" />
                  </span> : resource.resourceType || "Unknown"}
              </td>
              <td><Badge bg={resource.complianceScore == null ? "secondary" : resource.complianceScore >= 100 ? "success" : "warning"}
                text={resource.complianceScore != null && resource.complianceScore < 100 ? "dark" : undefined}>
                {resource.complianceScore == null ? "Unknown" : `${resource.complianceScore.toFixed(1)}%`}
              </Badge></td>
              <td className={styles.storageCell}>{formatBytes(resource.storageMb * 1024 * 1024)}</td>
              <td>{resource.missingFields || "No missing metadata reported"}<div className="text-muted">{resource.reviewStatus || "Review status unknown"}</div></td>
            </tr>)}</tbody>
          </Table>
        </div>
        {resources.length === 0 && <p className="text-muted mb-0">No owned resources recorded in this snapshot.</p>}
      </>}
    </Card.Body>
  </Card>;
}

export default function ResourceHealthDemoResults({ results, comparing }: { results: SnapshotResult[]; comparing: boolean }) {
  const current = results[0];
  const previous = results[1];
  if (!current) return null;
  return <div className={styles.results}>
    <div className={styles.summaryGrid}>
      {healthMetrics.filter((metric) => summaryKeys.has(metric.key)).map((metric) => {
        const value = current.person?.[metric.key];
        const prior = previous?.person?.[metric.key];
        const Icon = metric.kind === "storage" ? DatabaseFill : metric.kind === "count" ? PeopleFill : ExclamationCircleFill;
        return <Card key={metric.key} className={`${styles.summaryCard} ${styles[metric.kind]}`}>
          <Card.Body>
            {metric.kind === "score" ? <ScoreRing value={value} /> : <div className={styles.metricIcon}><Icon aria-hidden="true" /></div>}
            <div className={styles.summaryContent}>
              <h4>{metric.label}</h4>
              {metric.kind !== "score" && <div className={styles.metricValue}>{metric.format(value)}</div>}
              {previous && <Change metric={metric} current={value} previous={prior} />}
              <div className={styles.snapshotValue}>{previous ? "Latest" : "Selected"}: {metric.format(value)}</div>
              {previous && <div className={styles.snapshotValue}>Previous: {metric.format(prior)}</div>}
            </div>
          </Card.Body>
        </Card>;
      })}
    </div>
    <Card className={styles.comparisonPanel}>
      <Card.Body>
        <div className={styles.comparisonHeading}>
          <BarChartFill aria-hidden="true" />
          <h4>{previous ? "Detailed comparison" : "Snapshot health"}</h4>
          <span>{previous ? "How your Microsoft 365 resource health has changed between snapshots." : "Your Microsoft 365 resource health in this snapshot."}</span>
        </div>
        <Table bordered responsive size="sm" className={styles.comparisonTable}>
          <thead><tr><th>Health measure</th>{results.map((result, index) => <th key={result.snapshot.snapshotId}>
            {snapshotLabel(result.snapshot)} {comparing && <Badge bg={index === 0 ? "primary" : "secondary"}>{index === 0 ? "Latest" : "Previous"}</Badge>}
          </th>)}{previous && <th>Change</th>}<th>Visual comparison</th></tr></thead>
          <tbody>{healthMetrics.map((metric) => <tr key={metric.key}>
            <th scope="row">{metric.label}</th>
            {results.map((result) => <td key={result.snapshot.snapshotId}>{metric.format(result.person?.[metric.key])}</td>)}
            {previous && <td><Change metric={metric} current={current.person?.[metric.key]} previous={previous.person?.[metric.key]} /></td>}
            <td><VisualComparison metric={metric} results={results} /></td>
          </tr>)}</tbody>
        </Table>
        <p className={styles.footnote}>Historical snapshots, not live measurements. Change is latest minus previous. Resource count and storage changes are neutral, not a rating of improvement.
          {" "}Bars share a scale within each row; health scores use 0-100%. Dark bars show the first snapshot; pale bars show the previous snapshot. Missing data is not zero.</p>
      </Card.Body>
    </Card>
    <div className={`${styles.resourceGrid} ${results.length === 1 ? styles.singleSnapshot : ""}`}>
      {results.map((result, index) => <ResourcePanel key={result.snapshot.snapshotId} result={result} previous={index > 0} />)}
    </div>
  </div>;
}
